import React from "react";

/**
 * Circular gauge for speed values.
 * Props:
 *  - value: number (current mbps)
 *  - max: number (gauge maximum)
 *  - label: string (e.g. "Download")
 *  - unit: string
 *  - active: bool (currently measuring)
 *  - color: accent color for arc
 */
export function Gauge({ value = 0, max = 300, label = "", unit = "Mbps", active = false, color = "#5C85FF", testid }) {
  const clamped = Math.max(0, Math.min(value, max));
  // Log-scale feels more natural for speed gauges
  const pct = max > 0 ? Math.sqrt(clamped / max) : 0;
  const size = 220;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  // Arc spans 270deg (3/4 circle)
  const arcLen = circ * 0.75;
  const dash = arcLen * pct;
  const gap = circ - dash;

  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
          {/* Background track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(138,160,255,0.08)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${arcLen} ${circ}`}
            transform={`rotate(135 ${size / 2} ${size / 2})`}
          />
          {/* Value arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={`url(#grad-${label})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${gap}`}
            transform={`rotate(135 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dasharray 0.35s cubic-bezier(0.2,0.7,0.2,1)", filter: `drop-shadow(0 0 12px ${color}80)` }}
          />
          <defs>
            <linearGradient id={`grad-${label}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4A78FF" />
              <stop offset="100%" stopColor={color} />
            </linearGradient>
          </defs>
          {/* Tick marks */}
          {Array.from({ length: 13 }).map((_, i) => {
            const a = (135 + (i * 270) / 12) * (Math.PI / 180);
            const x1 = size / 2 + (r - stroke / 2 - 4) * Math.cos(a);
            const y1 = size / 2 + (r - stroke / 2 - 4) * Math.sin(a);
            const x2 = size / 2 + (r + 6) * Math.cos(a);
            const y2 = size / 2 + (r + 6) * Math.sin(a);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(138,160,255,0.18)" strokeWidth={1} />;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs font-mono uppercase tracking-[0.15em] text-[var(--text-muted)]">{label}</div>
          <div className={`font-display text-5xl font-semibold tabular-nums ${active ? "text-white" : "text-white"}`} style={{ textShadow: active ? `0 0 32px ${color}80` : "none" }}>
            {clamped < 10 ? clamped.toFixed(2) : clamped < 100 ? clamped.toFixed(1) : Math.round(clamped)}
          </div>
          <div className="text-sm text-[var(--text-dim)] font-mono">{unit}</div>
        </div>
        {active && (
          <div className="absolute inset-0 rounded-full pointer-events-none" style={{ boxShadow: `0 0 80px -20px ${color}` }} />
        )}
      </div>
    </div>
  );
}
